import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { Calendar, User, ArrowRight } from 'lucide-react';
import { blogArticles } from '../data/blogArticles';
const ARTICLES_PER_PAGE = 6;
export function BlogPage() {
  const [currentPage, setCurrentPage] = useState(1);
  // Calculate pagination
  const totalPages = Math.ceil(blogArticles.length / ARTICLES_PER_PAGE);
  const startIndex = (currentPage - 1) * ARTICLES_PER_PAGE;
  const endIndex = startIndex + ARTICLES_PER_PAGE;
  const currentArticles = blogArticles.slice(startIndex, endIndex);
  // Generate page numbers to display
  const getPageNumbers = () => {
    if (totalPages <= 6) {
      // Show all pages if 6 or fewer
      return Array.from({
        length: totalPages
      }, (_, i) => i + 1);
    } else {
      // Show 1 2 3 ... 6 pattern for more than 6 pages
      return [1, 2, 3, '...', totalPages];
    }
  };
  const pageNumbers = getPageNumbers();
  // Handle page change and scroll to top
  const handlePageChange = (pageNum: number) => {
    setCurrentPage(pageNum);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen w-full bg-white selection:bg-[#0066FF] selection:text-white" data-id="element-1934">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100" data-id="element-1935">
        <div className="w-full px-6 md:px-12 lg:px-24" data-id="element-1936">
          <div className="max-w-7xl mx-auto h-20 flex items-center" data-id="element-1937">
            <Link to="/" data-id="element-1938">
              <img src="https://cdn.magicpatterns.com/uploads/9qJGNjQNSV94689CCZT1kr/newTM_black_copy.png" alt="Synthia" className="h-8" data-id="element-1939" />
            </Link>
            <Navigation data-id="element-1940" />
          </div>
        </div>
      </header>

      <main className="pt-20" data-id="element-1941">
        {/* Hero Section */}
        <section className="py-16 px-6 md:px-12 lg:px-24 bg-white" data-id="element-1942">
          <div className="max-w-7xl mx-auto" data-id="element-1943">
            <div className="inline-flex items-center px-3 py-1 rounded-full border border-gray-200 bg-gray-50 mb-8 w-fit" data-id="element-1944">
              <span className="text-xs font-medium text-gray-600 uppercase tracking-wider font-sans" data-id="element-1945">
                SIGNALS
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-black mb-6 leading-tight font-['Syne']" data-id="element-1946">
              Insights on Engineering Context
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed max-w-4xl font-light" data-id="element-1947">
              Exploring the intersection of institutional intelligence, AI
              governance, and enterprise systems. How to build AI that
              understands your business, not just your data.
            </p>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="py-16 px-6 md:px-12 lg:px-24 bg-white" data-id="element-1948">
          <div className="max-w-7xl mx-auto" data-id="element-1949">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1950">
              {currentArticles.map(article => <article key={article.id} className="group border border-gray-200 rounded-lg overflow-hidden hover:border-[#0066FF] transition-all duration-200 hover:shadow-lg bg-white" data-id="element-1951">
                  {/* Article Image */}
                  <div className="w-full h-48 bg-gray-100 overflow-hidden" data-id="element-1952">
                    <img src={article.image} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" data-id="element-1953" />
                  </div>

                  {/* Category Badge */}
                  <div className="p-6 pb-0" data-id="element-1954">
                    <span className="inline-block px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#0066FF] bg-blue-50 rounded-full" data-id="element-1955">
                      {article.category}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="p-6" data-id="element-1956">
                    <h2 className="text-2xl font-bold text-black mb-3 leading-tight font-['Syne'] group-hover:text-[#0066FF] transition-colors" data-id="element-1957">
                      {article.title}
                    </h2>

                    <p className="text-gray-600 leading-relaxed mb-4 font-light" data-id="element-1958">
                      {article.excerpt}
                    </p>

                    {/* Meta Info */}
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-4" data-id="element-1959">
                      <div className="flex items-center gap-1" data-id="element-1960">
                        <Calendar className="w-4 h-4" data-id="element-1961" />
                        <span data-id="element-1962">{article.date}</span>
                      </div>
                      <div className="flex items-center gap-1" data-id="element-1963">
                        <User className="w-4 h-4" data-id="element-1964" />
                        <span data-id="element-1965">{article.author}</span>
                      </div>
                    </div>

                    <Link to={`/blog/${article.slug}`} className="inline-flex items-center gap-2 text-[#0066FF] font-bold text-sm uppercase tracking-wide hover:gap-3 transition-all duration-200 font-['Syne']" data-id="element-1966">
                      Read Article
                      <ArrowRight className="w-4 h-4" data-id="element-1967" />
                    </Link>
                  </div>
                </article>)}
            </div>

            {/* Pagination - Always show, even for single page */}
            <div className="flex justify-center items-center gap-2 mt-12" data-id="element-1968">
              {pageNumbers.map((pageNum, index) => {
              if (pageNum === '...') {
                return <span key={`ellipsis-${index}`} className="px-4 py-2 text-gray-400 font-bold" data-id="element-1969">
                      ...
                    </span>;
              }
              const isActive = pageNum === currentPage;
              return <button key={pageNum} onClick={() => handlePageChange(pageNum as number)} disabled={totalPages === 1} className={`px-4 py-2 font-bold font-['Syne'] transition-all duration-200 rounded ${isActive ? 'bg-[#0066FF] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} ${totalPages === 1 ? 'cursor-default' : 'cursor-pointer'}`} data-id="element-1970">
                    {pageNum}
                  </button>;
            })}
            </div>
          </div>
        </section>

        {/* Newsletter CTA */}
        <section className="py-16 px-6 md:px-12 lg:px-24 bg-gray-50 border-t border-gray-200" data-id="element-1971">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1972">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4 font-['Syne']" data-id="element-1973">
              Stay Updated
            </h2>
            <p className="text-lg text-gray-600 mb-8 font-light" data-id="element-1974">
              Get insights on context engineering, AI governance, and enterprise
              intelligence delivered to your inbox.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto" data-id="element-1975">
              <input type="email" placeholder="Enter your email" className="flex-1 px-4 py-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#0066FF] focus:border-transparent" data-id="element-1976" />
              <button className="px-8 py-3 bg-[#0066FF] text-white font-['Syne'] font-bold text-sm tracking-wide uppercase hover:bg-black transition-colors" data-id="element-1977">
                Subscribe
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer data-id="element-1978" />
    </div>;
}